import {
  IFrameInfo,
  IWebContentMessageEvent,
  IWebContentRequestMessage,
  IWebContentResponseMessage,
  Themes,
  UserSettingTypes,
} from './messages.interface';
import {
  AppContext,
  AppInitInfo,
  AppInitState,
  IAppDefinition,
  ICommunicationBridge,
  IHostedWebContentSdkService,
  IWebContentContext,
} from './hosted-web-content-sdk.interface';
import { IWindowProvider } from './event-listener.interface';
import { formatUrl, getValidatedResources, isValidUrlForApp, removeDefaultPort, transformContext } from './utils';
import { IMessageProcessor } from './MessageHandling/messaging-infra.interface';
import { MessageProcessor } from './MessageHandling/message-processor';
import { WebContentSdkService } from './web-content-sdk';
import { IMetaOsHubServices } from './interfaces';
import { TelemetrySink } from '@microsoft/oteljs';

export class HostedWebContentSdkService extends WebContentSdkService implements IHostedWebContentSdkService {
  protected origin?: string;
  protected frame?: IFrameInfo;
  protected messageProcessor?: IMessageProcessor;
  public onThemeChanged?: (newTheme: Themes) => void;
  public onUserSettingsChanged?: (type: UserSettingTypes, value: string) => void;

  constructor(
    // TODO: Resolve inability to access global window through IWindowProvider https://domoreexp.visualstudio.com/MSTeams/_workitems/edit/746287
    protected hostWindow: Window | IWindowProvider,
    protected communicationBridge: ICommunicationBridge,
    protected context: IWebContentContext,
    protected hubServices: IMetaOsHubServices,
    protected onAppInitStateChanged?: (appInitState: AppInitInfo) => void,
    protected telemetrySink?: TelemetrySink
  ) {
    super(
      hostWindow,
      context,
      hubServices.authenticationService,
      hubServices.telemetryService,
      onAppInitStateChanged,
      telemetrySink
    );
    /* init auth context before loading any app url in iframe : 
       This ensures auth Service to be linked in for user details before use Context for placeholding
    */
    this.initializeAuthenticationContext(context);
    communicationBridge.onSdkInitialized && communicationBridge.onSdkInitialized(hostWindow);
  }

  /**
   * initialize frame and load target origin to frame
   * @param frame: target frame
   * @param origin: target origin
   * @param appDefinition: Hosted web content app definition
   */
  public async setFrameAndLoadOrigin(
    frame: IFrameInfo | undefined,
    origin: string,
    appDefinition: IAppDefinition,
    userClickTime: number
  ): Promise<void> {
    this.setAppInitInfo({ state: AppInitState.Loading });
    this.setInitialized(false);

    this.telemetryService.setAppDefinition(appDefinition);
    this.appDefinition = appDefinition;
    this.frame = frame;

    this.setUserClickTime(userClickTime);

    if (this.messageProcessor) {
      this.messageProcessor.cleanup();
    }

    this.messageProcessor = new MessageProcessor(this, this.hubServices, this.telemetryService);

    // prefetch token asynchronously
    this.prefetchAuthToken(appDefinition, origin);

    // Establish communication before loading url
    if (frame) {
      this.communicationBridge.onSetFrameAndLoadOrigin(frame, origin, appDefinition, this.handleMessage.bind(this));
    }

    return this.load(origin).then(() => {
      // if the application is not using native loading indicator flow, we will assume it loaded at this point
      if (!appDefinition.showTabLoadingIndicator) {
        this.setAppInitInfo({ state: AppInitState.Loaded });
      }
    });
  }

  /**
   * to send request message to frame
   * @param functionName: function name
   * @param funArguments: function arguments
   */
  public sendRequestMessage(functionName: string, funArguments?: (object | string)[]): void {
    if (this.origin && this.frame) {
      const request: IWebContentRequestMessage = {
        func: functionName,
        args: funArguments,
      };
      this.communicationBridge.postMessage(this.frame, request);
    }
  }

  /**
   * to send response message to frame
   * @param target: target
   * @param id: id used for callback function
   * @param args: function arguments
   */
  public sendResponseMessage(
    target: IFrameInfo,
    id: number | undefined,
    args?: (object | string | boolean)[],
    isPartialResponse?: boolean
  ): void {
    if (this.origin) {
      const response: IWebContentResponseMessage = {
        id,
        args,
        isPartialResponse,
        origin: this.origin,
      };
      this.communicationBridge.postMessage(target, response);
    }
  }

  public getAppDefinition = (): IAppDefinition | undefined => this.appDefinition;

  /**
   * Request authentication token(s) during loading
   * @param appDefinition
   * @param origin
   * @returns
   * prefetchAuthToken does not return an auth token. It only calls an authentication service acquireToken method.
   * We are expecting a Hubs authentication service to handle the caching of tokens.
   */
  public prefetchAuthToken = async (appDefinition: IAppDefinition, origin: string): Promise<boolean> => {
    const scenario = this.telemetryService?.createScenario('preFetchAuthToken', appDefinition?.appId);
    if (this.hubServices.authenticationService) {
      try {
        const context = await this.getAppContext();
        const resources = await getValidatedResources(origin, [], this.getHubInfo(), appDefinition, context);
        // Hub's authentication service handles token fetch and caching
        await this.hubServices.authenticationService.acquireToken(resources[0]);
        scenario?.stop();
        return true;
      } catch (ex) {
        const errorMessage = 'Unable to prefetch token via authentication service: ' + ex;
        scenario?.fail(errorMessage);
      }
    } else {
      const errorMessage = 'Cancelled since authenticationService is not available';
      scenario?.fail(errorMessage);
    }
    return false;
  };

  /**
   * to send response message to app frame
   * @param id: id used for callback function
   * @param args: function arguments
   * @param isPartialResponse: is response partial
   */
  public postResponseToApp(
    id: number | undefined,
    args?: (object | string | boolean)[],
    isPartialResponse?: boolean
  ): void {
    if (this.frame) {
      this.sendResponseMessage(this.frame, id, args, isPartialResponse);
    }
  }

  protected urlToLoad = (formattedUrl: string) => formattedUrl;

  /**
   * handles the given message event using one of the built in SDK message
   * handlers, or one previously provided via `addMessageHandler`
   *
   * @protected
   * @param {IWebContentMessageEvent} event
   * @memberof HostedWebContentSdkService
   */
  protected handleMessage(event: IWebContentMessageEvent): void {
    if (!this.messageProcessor) {
      throw new Error('Unable to handle message. Undefined message processor');
    }
    this.messageProcessor.process(event);
  }

  /**
   * load origin to frame
   * @param url: target url
   */
  public load(url: string): Promise<void> {
    return this.getAppContext().then((appContext: AppContext) => {
      if (isValidUrlForApp(url, this.getAppDefinition(), appContext) && this.frame) {
        const formattedUrl = this.tryFormattingUrl(url, appContext);
        this.origin = formattedUrl;
        this.frame.src = removeDefaultPort(this.urlToLoad(formattedUrl));
        return Promise.resolve();
      } else {
        var error = 'Url is invalid';
        return Promise.reject(error);
      }
    });
  }

  private tryFormattingUrl(url: string, appContext: AppContext): string {
    let formattedUrl = formatUrl(url, appContext, []);

    //  check for old Context patterns for backward compatibilty
    const oldContextObject = transformContext(appContext);
    formattedUrl = formatUrl(formattedUrl, oldContextObject, []);

    return formattedUrl;
  }

  /**
   * function to execute when app container is unmounted
   */
  public unloadSDK(): void {
    this.communicationBridge.dispose();
  }
}
